import { Dispatch, SetStateAction, createContext, useContext, useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { findKey } from '@npi/buddy-utils';
import { PricingType, ProductType } from '@prisma/client';
import { useField } from 'formik';

import { showErrorToast } from 'src/components/Toasts/toast';
import { GET_PRICING_OFFERS } from 'src/graphql/queries/pricing.query';
import { formatProductPricing } from 'src/hooks/useServiceTypeOffers/utils';
import { Order } from 'src/pages/AddMerchantPage/components/OrdersList';
import {
    PricingServiceTypes,
    PricingTerms,
} from 'src/pages/AddMerchantPage/components/ServiceType';
import { ProductOrder } from 'src/types/AddMerchant/serviceRequirements';

import { useAddMerchantContext } from '../AddMerchantProvider';
import { useProductOrderContext } from '../ProductOrderProvider';

enum FeeProgramme {
    PER_ITEM = 'PER_ITEM',
    FIXED = 'FIXED',
}

type SalesCommisions = {
    sales: number;
    commission: number | null;
};

type ProductPricing = {
    oneOff: SalesCommisions[];
    monthly: SalesCommisions[];
};

export type OfferProduct = {
    id: number;
    name: string;
    type?: ProductType;
    imageUrl?: string;
    price?: number;
    description?: string;
    options?: ProductPricing;
};

type OfferProductsType = {
    primaryProducts: OfferProduct[];
    secondaryProducts: OfferProduct[];
};

export type ServiceOrder = {
    primary: Order[];
    secondary: Order[];
};

export type PricingOffer = {
    id: number;
    name: string;
    term?: PricingTerms;
    serviceType?: PricingServiceTypes;
    cost?: number;
    offerId?: string;
    primaryProducts: OfferProduct[];
    secondaryProducts: OfferProduct[];
    pciComplianceFeeProgramme?: FeeProgramme;
    pciComplianceRate?: number;
    type: PricingType;
};

type SelectedProducts = {
    primary: any[];
    secondary: any[];
};

type Total = {
    id: number;
    price: number;
    quantity: number;
};

type Totals = {
    totalOneOff: Total[];
    totalMonthly: Total[];
};

interface ServiceOrderProvider {
    offerProducts: OfferProductsType;
    setOfferProducts: Dispatch<SetStateAction<OfferProductsType>>;
    contractTerm: PricingTerms;
    setContractTerm: Dispatch<SetStateAction<PricingTerms>>;
    getOfferData: any;
    offerData: any[];
    selectedProducts: SelectedProducts;
    setSelectedProducts: Dispatch<SetStateAction<SelectedProducts>>;
    totalOrderPrice: Totals;
    setTotalOrderPrice: Dispatch<SetStateAction<Totals>>;
    partnerId: number;
    setPartnerId: Dispatch<SetStateAction<number>>;
    selectedOfferId: number;
    setSelectedOfferId: Dispatch<SetStateAction<number>>;
}

const ServiceOrderContext = createContext<Partial<ServiceOrderProvider>>({});

export const useServiceOrderContext = () => {
    return useContext(ServiceOrderContext);
};

export const ServiceOrderProvider: React.FC = ({ children }) => {
    const { merchantApplication, serviceApplication } = useAddMerchantContext();
    const { onClearOrder } = useProductOrderContext();
    const [offerData, setOfferData] = useState([]);
    // SERVICE TYPE
    const [{ value: serviceType }] = useField({
        name: 'service.serviceType',
    });
    const [{ value: contractTermField }, , { setValue: setContractTermField }] = useField({
        name: 'service.contractTerm',
    });
    const [{ value: selectedTerminalOffer }, , { setValue: setSelectedTerminalOffer }] = useField({
        name: 'service.selectedTerminalOffer',
    });
    const [{ value: selectedOnlineOffer }, , { setValue: setSelectedOnlineOffer }] = useField({
        name: 'service.selectedOnlineOffer',
    });
    // SERVICE ORDER
    const [{ value: serviceOrder }, , { setValue: setServiceOrder }] = useField<ServiceOrder>({
        name: 'order',
    });
    // PRODUCT ORDER TOTALS
    const [{ value: productOrder }, , { setValue: setProductOrder }] = useField<ProductOrder>({
        name: 'productOrder',
    });
    const [{ value: totals }, , { setValue: setTotals }] = useField({
        name: 'totals',
    });

    const [getOfferData] = useLazyQuery(GET_PRICING_OFFERS, {
        onError: (error) => {
            showErrorToast(error.message);
        },
        onCompleted: (offerData) => {
            setOfferData(offerData?.pricingOffers);
        },
    });

    const [offerProducts, setOfferProducts] = useState<OfferProductsType>({
        primaryProducts: [] as OfferProduct[],
        secondaryProducts: [] as OfferProduct[],
    });

    const [selectedProducts, setSelectedProducts] = useState<SelectedProducts>({
        primary: [],
        secondary: [],
    });

    const [totalOrderPrice, setTotalOrderPrice] = useState({
        totalOneOff: [],
        totalMonthly: [],
    });

    const [contractTerm, setContractTerm] = useState();

    const [partnerId, setPartnerId] = useState();

    const [selectedOfferId, setSelectedOfferId] = useState();

    useEffect(() => {
        // Initialise totalOrderPrice with application values if exists
        if (serviceApplication) {
            if (
                serviceApplication?.serviceRequirements?.totalOrderPrice?.totalMonthly?.length >
                    0 ||
                serviceApplication?.serviceRequirements?.totalOrderPrice?.totalMonthly?.length > 0
            ) {
                setTotalOrderPrice(serviceApplication?.serviceRequirements?.totalOrderPrice);
            }
        } else {
            if (
                merchantApplication?.serviceRequirements?.totalOrderPrice?.totalMonthly?.length >
                    0 ||
                merchantApplication?.serviceRequirements?.totalOrderPrice?.totalMonthly?.length > 0
            ) {
                setTotalOrderPrice(merchantApplication?.serviceRequirements?.totalOrderPrice);
            }
        }
    }, [merchantApplication, serviceApplication]);

    useEffect(() => {
        if (
            offerData &&
            serviceType &&
            contractTerm &&
            (selectedTerminalOffer || selectedOnlineOffer)
        ) {
            const selectedOfferId = selectedOnlineOffer || selectedTerminalOffer;

            const selectedOffer = offerData?.find((offer) => {
                return offer?.id?.toString() === selectedOfferId;
            });

            if (selectedOffer) {
                setOfferProducts({
                    primaryProducts: selectedOffer?.primaryProducts?.map((product) => {
                        return formatProductPricing(product, selectedOffer);
                    }),
                    secondaryProducts: selectedOffer?.secondaryProducts?.map((product) => {
                        return formatProductPricing(product, selectedOffer);
                    }),
                });
            }
        }
    }, [offerData, serviceType, contractTerm, selectedTerminalOffer, selectedOnlineOffer]);

    useEffect(() => {
        if (
            partnerId &&
            serviceType &&
            contractTerm &&
            (selectedTerminalOffer || selectedOnlineOffer)
        ) {
            getOfferData({
                variables: {
                    filter: {
                        type: findKey(PricingServiceTypes, (o) => {
                            return o === serviceType;
                        }),
                        term: findKey(PricingTerms, (o) => {
                            return o === contractTerm;
                        }),
                        partnerId,
                    },
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        serviceType,
        contractTerm,
        selectedTerminalOffer,
        selectedOnlineOffer,
        partnerId,
        offerData,
    ]);
    const onContractTermChange = () => {
        // If contract term - reset selected offer
        setSelectedTerminalOffer('');
        setSelectedOnlineOffer('');

        // Reset totals
        setTotals({
            totalOneOff: 0,
            totalMonthly: 0,
            totalTerm: 0,
        });

        // Reset product order
        setProductOrder({
            term: 0,
            total: 0,
            totalSetup: 0,
            primaryProducts: [],
            secondaryProducts: [],
            totalPrimaryMonthly: 0,
            totalSecondaryMonthly: 0,
        });
        // Reset local state of ProductOrder
        onClearOrder();

        // Reset service order
        setServiceOrder({
            primary: [],
            secondary: [],
        });

        setTotalOrderPrice({
            totalOneOff: [],
            totalMonthly: [],
        });

        setSelectedProducts({
            primary: [],
            secondary: [],
        });
    };
    useEffect(() => {
        if (serviceApplication) {
            if (
                serviceApplication?.serviceRequirements?.service?.contractTerm !== contractTermField
            ) {
                onContractTermChange();
            }
        } else {
            if (
                merchantApplication?.serviceRequirements?.service?.contractTerm !==
                contractTermField
            ) {
                onContractTermChange();
            }
        }
    }, [
        onClearOrder,
        contractTermField,
        merchantApplication,
        setSelectedOnlineOffer,
        setSelectedTerminalOffer,
        setTotals,
        setProductOrder,
        setServiceOrder,
        serviceApplication,
    ]);
    const onServiceTypeChange = () => {
        // If serviceType changes - reset selected offer and contract term
        setSelectedTerminalOffer('');
        setSelectedOnlineOffer('');
        setContractTermField('');

        // Reset Totals
        setTotals({
            totalOneOff: 0,
            totalMonthly: 0,
            totalTerm: 0,
        });

        // Reset product order
        setProductOrder({
            total: 0,
            primaryProducts: [],
            secondaryProducts: [],
            term: 0,
            totalSecondaryMonthly: 0,
            totalPrimaryMonthly: 0,
            totalSetup: 0,
        });
        // Reset local state of ProductOrder
        onClearOrder();

        // Reset service order
        setServiceOrder({
            primary: [],
            secondary: [],
        });

        setTotalOrderPrice({
            totalOneOff: [],
            totalMonthly: [],
        });

        setSelectedProducts({
            primary: [],
            secondary: [],
        });
    };
    useEffect(() => {
        if (serviceApplication) {
            if (serviceApplication?.serviceRequirements?.service?.serviceType !== serviceType) {
                onServiceTypeChange();
            }
        } else {
            if (merchantApplication?.serviceRequirements?.service?.serviceType !== serviceType) {
                onServiceTypeChange();
            }
        }
    }, [
        onClearOrder,
        serviceType,
        merchantApplication,
        serviceApplication,
        setContractTermField,
        setSelectedOnlineOffer,
        setSelectedTerminalOffer,
        setTotals,
        setProductOrder,
        setServiceOrder,
    ]);
    const onSelectedTerminalOfferSelectedOnlineOfferChange = () => {
        // If selectedTerminalOffer or selectedOnlineOffer changes - reset totals and product order

        // Reset Totals
        setTotals({
            totalOneOff: 0,
            totalMonthly: 0,
            totalTerm: 0,
        });

        // Reset product order
        setProductOrder({
            total: 0,
            primaryProducts: [],
            secondaryProducts: [],
            term: 0,
            totalSecondaryMonthly: 0,
            totalPrimaryMonthly: 0,
            totalSetup: 0,
        });
        // Reset local state of ProductOrder
        onClearOrder();

        // Reset service order
        setServiceOrder({
            primary: [],
            secondary: [],
        });

        setTotalOrderPrice({
            totalOneOff: [],
            totalMonthly: [],
        });

        setSelectedProducts({
            primary: [],
            secondary: [],
        });
    };
    useEffect(() => {
        if (serviceApplication) {
            if (
                serviceApplication?.serviceRequirements?.service?.selectedTerminalOffer !==
                    selectedTerminalOffer ||
                serviceApplication?.serviceRequirements?.service?.selectedOnlineOffer !==
                    selectedOnlineOffer
            ) {
                onSelectedTerminalOfferSelectedOnlineOfferChange();
            }
        } else {
            if (
                merchantApplication?.serviceRequirements?.service?.selectedTerminalOffer !==
                    selectedTerminalOffer ||
                merchantApplication?.serviceRequirements?.service?.selectedOnlineOffer !==
                    selectedOnlineOffer
            ) {
                onSelectedTerminalOfferSelectedOnlineOfferChange();
            }
        }
    }, [
        onClearOrder,
        merchantApplication,
        serviceApplication,
        selectedTerminalOffer,
        selectedOnlineOffer,
        setTotals,
        setProductOrder,
        setServiceOrder,
    ]);

    return (
        <ServiceOrderContext.Provider
            value={{
                offerProducts,
                setOfferProducts,
                contractTerm,
                setContractTerm,
                getOfferData,
                offerData,
                selectedProducts,
                setSelectedProducts,
                totalOrderPrice,
                setTotalOrderPrice,
                partnerId,
                setPartnerId,
                selectedOfferId,
                setSelectedOfferId,
            }}
        >
            {children}
        </ServiceOrderContext.Provider>
    );
};
